import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Send, ShieldCheck, BookMarked, Newspaper, MessagesSquare, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageIntro from "@/components/PageIntro";
import { Reveal, EASE } from "@/components/landing/Reveal";
import { recordingContributionDraft } from "@/lib/recordingContribution";
import { ABOUT_AUTHOR } from "@/data/aboutMission";

const CATEGORIES = [
  { id: "correction", icon: BookMarked, label: "Correction to an entry", desc: "Fix a date, source, or wording." },
  { id: "research", icon: MessagesSquare, label: "Research contribution", desc: "Offer material on an apparition or feast." },
  { id: "media", icon: Newspaper, label: "Recordings / media", desc: "Volunteer singing, translations, permissions." },
  { id: "other", icon: Mail, label: "Other", desc: "Something else — every note is read." },
];

export default function ContactPage() {
  const location = useLocation();
  return <ContactForm key={location.search} contribution={recordingContributionDraft(location.search)} />;
}

function ContactForm({ contribution }) {
  const [category, setCategory] = useState(contribution ? "media" : "correction");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState(contribution?.subject || "");
  const [message, setMessage] = useState(contribution?.message || "");
  const [website, setWebsite] = useState(""); // honeypot
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState(null);
  const [receipt, setReceipt] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    const invalid = !name.trim() || name.trim().length > 120 ? ["contact-name", "Please enter your name (up to 120 characters)."]
      : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) || email.trim().length > 254 ? ["contact-email", "Please enter a valid email address."]
      : !subject.trim() || subject.trim().length > 180 || /[\r\n]/.test(subject) ? ["contact-subject", "Please enter a subject of 1–180 characters on one line."]
      : message.trim().length < 10 || message.trim().length > 4000 ? ["contact-message", "Please write a message of 10–4000 characters."] : null;
    if (invalid) {
      setFormError({ field: invalid[0], message: invalid[1] });
      e.currentTarget.querySelector(`[data-testid="${invalid[0]}"]`)?.focus();
      return;
    }
    setFormError(null);
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), category, subject: subject.trim(), message: message.trim(), website }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = Array.isArray(data?.detail)
          ? data.detail.map((d) => d.msg || d).join(", ")
          : (typeof data?.detail === "string" ? data.detail : null);
        throw new Error(detail || "Something went wrong. Please try again.");
      }
      toast.success(data.message || "Message received.");
      setReceipt(data);
      setDone(true);
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
    } catch (err) {
      toast.error(err.message || "Could not send just now. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <Seo
        title="Contact — corrections, research, and questions · Theotokos · Mater Dei"
        siteName="Theotokos · Mater Dei"
        description="Contact Theotokos Mater Dei, part of The Gate to Heaven Ministry, with questions, source corrections, research contributions or recording offers."
      />
      <PageIntro
        crumbs={[{ label: "Home", to: "/" }, { label: "About", to: "/about" }, { label: "Contact" }]}
        eyebrow="Write to us · corrections and research"
        lines={["A letter to", "the treasury"]}
        aside={
          <div className="rounded-2xl border border-marian/15 bg-white/70 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Our promise</p>
            <p className="mt-2 font-serif text-lg leading-snug text-midnight">Every note is read.</p>
            <p className="mt-2 text-xs leading-relaxed text-slateblue">
              We never sell blessings, promises, or spiritual favours — and we never sell your address.
              Corrections are made promptly and noted on the page.
            </p>
          </div>
        }
      >
        <span data-testid="contact-ministry-affiliation" className="mb-4 block text-sm leading-relaxed text-marian">
          Theotokos Mater Dei is part of <a href={ABOUT_AUTHOR.ministryUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-ministry-link" className="font-semibold underline decoration-gold/60 underline-offset-4 transition-colors hover:text-midnight focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">{ABOUT_AUTHOR.ministry}</a>.
        </span>
        Corrections, research contributions, translation and media enquiries — all of it lands
        in one inbox and every message is read personally.
      </PageIntro>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-8 sm:py-20 lg:px-16">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="min-w-0">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">01 · Choose a topic</p>
              <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-midnight">What brings you to write?</h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-charcoal/85">
                Selecting the closest topic helps us route your message quickly and respond with the
                right care and sources.
              </p>
            </Reveal>
            <ul className="mt-8 space-y-3" role="radiogroup" aria-label="Message category" data-testid="category-radiogroup">
              {CATEGORIES.map((c, i) => {
                const active = category === c.id;
                const Icon = c.icon;
                return (
                  <Reveal key={c.id} delay={i * 0.05}>
                    <li>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setCategory(c.id)}
                        data-testid={`contact-category-${c.id}`}
                        className={`group flex w-full items-start gap-4 rounded-2xl border p-4 text-left transition-all duration-300 ${
                          active
                            ? "border-marian bg-white shadow-[0_18px_40px_-28px_rgba(31,78,121,0.7)]"
                            : "border-marian/15 bg-white/70 hover:border-marian/40"
                        }`}
                      >
                        <span
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors ${
                            active ? "bg-marian text-ivory" : "bg-marian/10 text-marian group-hover:bg-marian/15"
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="flex-1">
                          <span className="block font-serif text-lg font-semibold text-midnight">{c.label}</span>
                          <span className="mt-0.5 block text-xs leading-relaxed text-slateblue">{c.desc}</span>
                        </span>
                        <span
                          className={`mt-1 h-4 w-4 shrink-0 rounded-full border-2 transition-colors ${
                            active ? "border-marian bg-marian" : "border-marian/30"
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                    </li>
                  </Reveal>
                );
              })}
            </ul>

            <Reveal delay={0.15}>
              <div className="mt-10 rounded-2xl border border-marian/15 bg-paleblue/40 p-5">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                  <ShieldCheck className="h-3.5 w-3.5" /> Before you write
                </p>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/85">
                  If you are proposing a correction, please include the page URL and the specific
                  sentence. If you are offering research, one primary Church source (a bishop's decree,
                  a shrine document, a Holy See text) helps us evaluate quickly.
                </p>
                <Link to="/editorial-standards" data-testid="contact-standards-link" className="group mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-marian hover:text-midnight">
                  Read our editorial standards
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>

          <div id="contact-message-form" className="min-w-0 scroll-mt-28">
            {contribution && <section data-testid="recording-contribution-context" className="mb-8 border-l-2 border-gold pl-5">
              <h2 data-testid="recording-contribution-title" className="font-serif text-lg font-semibold text-midnight">Offer your voice in prayer</h2>
              <p data-testid="recording-contribution-prayer" className="mt-3 break-words text-sm text-marian">{contribution.prayer} · {contribution.language}</p>
              <p data-testid="recording-contribution-guidance" className="mt-3 text-sm leading-relaxed">Contributions are voluntary and unpaid. Please follow the published prayer text, use human voices, and tell us who owns the recording, music and accompaniment. Obtain permission from every performer. Include a listening link if you have one; we will discuss permission and credits before publication. Sending a message does not automatically publish or license your recording.</p>
              <Link to={contribution.from} data-testid="recording-contribution-return" className="mt-3 inline-block text-xs text-marian underline">Return to the prayer</Link>
            </section>}
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">02 · Your message</p>
              <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-midnight">Write to the editors</h2>
            </Reveal>

            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                data-testid="contact-success"
                className="mt-8 overflow-hidden rounded-3xl border border-marian/15 bg-white shadow-[0_28px_60px_-40px_rgba(16,42,67,0.5)]"
              >
                <div className="gold-rule" />
                <div className="p-8">
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Received in peace</p>
                  <p className="mt-4 font-serif text-2xl leading-snug text-midnight">
                    {receipt?.message || "Thank you — your message has been received."}
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-charcoal/80">
                    Ad Iesum per Mariam.
                  </p>
                  <button
                    type="button"
                    onClick={() => setDone(false)}
                    data-testid="contact-write-another"
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-marian hover:text-midnight"
                  >
                    Write another
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="mt-8 overflow-hidden rounded-3xl border border-marian/15 bg-white shadow-[0_28px_60px_-40px_rgba(16,42,67,0.5)]"
                noValidate
              >
                <div className="gold-rule" />
                <div className="grid gap-5 p-6 sm:p-8">
                  {formError && <p id="contact-form-error" role="alert" data-testid="contact-form-error" className="border-l-2 border-crimson pl-4 text-sm text-crimson">{formError.message}</p>}
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Your name" htmlFor="c-name">
                      <input
                        id="c-name"
                        aria-invalid={formError?.field === "contact-name"} aria-describedby={formError?.field === "contact-name" ? "contact-form-error" : undefined}
                        required
                        maxLength={120}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="contact-name"
                        className="input"
                        placeholder="Maria Rossi"
                        autoComplete="name"
                      />
                    </Field>
                    <Field label="Email" htmlFor="c-email">
                      <input
                        id="c-email"
                        maxLength={254} aria-invalid={formError?.field === "contact-email"} aria-describedby={formError?.field === "contact-email" ? "contact-form-error" : undefined}
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="contact-email"
                        className="input"
                        placeholder="you@example.com"
                        autoComplete="email"
                      />
                    </Field>
                  </div>
                  <Field label="Subject" htmlFor="c-subject">
                    <input
                      id="c-subject"
                      aria-invalid={formError?.field === "contact-subject"} aria-describedby={formError?.field === "contact-subject" ? "contact-form-error" : undefined}
                      required
                      maxLength={180}
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      data-testid="contact-subject"
                      className="input"
                      placeholder="e.g. Fátima — clarification on the third secret text"
                    />
                  </Field>
                  <Field label="Your message" htmlFor="c-message" hint={`${message.length} / 4000 characters`}>
                    <textarea
                      id="c-message"
                      aria-invalid={formError?.field === "contact-message"} aria-describedby={formError?.field === "contact-message" ? "contact-form-error" : undefined}
                      required
                      minLength={10}
                      maxLength={4000}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={8}
                      data-testid="contact-message"
                      className="input resize-y"
                      placeholder="Please include page URL, sources, and any citation details — we take every note seriously."
                    />
                  </Field>

                  {/* Honeypot — hidden from users. */}
                  <div className="absolute -left-[9999px] top-auto h-0 w-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="c-website">Website</label>
                    <input id="c-website" tabIndex={-1} value={website} onChange={(e) => setWebsite(e.target.value)} autoComplete="off" />
                  </div>

                  <div className="flex flex-col-reverse items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs leading-relaxed text-slateblue">
                      We store your message privately and never sell your address.
                    </p>
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="contact-submit"
                      className="group inline-flex items-center gap-2 rounded-full bg-marian px-6 py-3 text-sm font-semibold text-ivory transition-all hover:bg-midnight disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Send className={`h-4 w-4 transition-transform duration-300 ${sending ? "animate-pulse" : "group-hover:-translate-y-0.5 group-hover:translate-x-0.5"}`} />
                      {sending ? "Sending…" : "Send message"}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

const Field = ({ label, htmlFor, hint, children }) => (
  <label htmlFor={htmlFor} className="block">
    <span className="flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.28em] text-marian">
      <span>{label}</span>
      {hint && <span className="text-slateblue">{hint}</span>}
    </span>
    <span className="mt-2 block">{children}</span>
  </label>
);
